/**
 * Pure presentation helpers for match history UI (no I/O).
 */

import { buildCardQuickStats } from "./buildMatchInsightStats";

/**
 * @param {string} matchType
 */
export function formatMatchTypeLabel(matchType) {
  const t = (matchType || "").toLowerCase();
  if (t === "doubles") return "Doubles";
  if (t === "mixed") return "Mixed doubles";
  if (t === "singles") return "Singles";
  return matchType ? matchType.charAt(0).toUpperCase() + matchType.slice(1) : "Match";
}

/**
 * @param {number | null | undefined} durationSec
 */
export function formatMatchDuration(durationSec) {
  if (durationSec == null || Number.isNaN(durationSec)) return null;
  const totalMin = Math.max(1, Math.round(durationSec / 60));
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * @param {number} completedAtMs
 */
export function formatMatchDate(completedAtMs) {
  if (!completedAtMs) return "";
  return new Date(completedAtMs).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * @param {number} completedAtMs
 */
export function formatMatchTime(completedAtMs) {
  if (!completedAtMs) return "";
  return new Date(completedAtMs).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * @param {number} completedAtMs
 */
export function formatMatchDateTime(completedAtMs) {
  if (!completedAtMs) return "";
  return new Date(completedAtMs).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Calendar day key for grouping (local timezone).
 * @param {number} completedAtMs
 */
export function matchHistoryDayKey(completedAtMs) {
  const d = new Date(completedAtMs || 0);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * @param {number} completedAtMs
 */
export function formatMatchHistoryGroupLabel(completedAtMs) {
  const d = new Date(completedAtMs || 0);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Immutable snapshot label for history UI (live profile is last resort only).
 * @param {import('./matchHistoryTypes').MatchParticipant} participant
 * @param {Record<string, string>} [nameByPlayerId]
 */
/**
 * Side label for result analysis.
 * Singles is one player. Doubles keeps "Name / Name". Repeated copies of the same name collapse.
 * @param {Array<string | null | undefined>} names
 * @param {{ singles?: boolean }} [options]
 */
export function formatSidePlayerLabel(names, options = {}) {
  const flat = [];
  const seen = new Set();
  for (const raw of names || []) {
    const parts = String(raw || "")
      .split(/\s*\/\s*/)
      .map((part) => part.trim())
      .filter(Boolean);
    for (const part of parts) {
      const key = part.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      flat.push(part);
    }
  }
  if (!flat.length) return "";
  if (options.singles) return flat[0];
  return flat.join(" / ");
}

export function participantDisplayName(participant, nameByPlayerId = {}) {
  const snapshot = (participant?.displayName || "").trim();
  if (snapshot) return snapshot;
  if (participant?.legacyPlayerName) return participant.legacyPlayerName;
  if (participant?.playerId && nameByPlayerId[participant.playerId]) {
    return nameByPlayerId[participant.playerId];
  }
  if (participant?.playerId) return "Player";
  return "Unknown";
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant} participant
 * @param {Record<string, string | null>} [avatarByPlayerId]
 */
export function participantAvatarUrl(participant, avatarByPlayerId = {}) {
  if (participant?.avatar) return participant.avatar;
  if (participant?.playerId && avatarByPlayerId[participant.playerId]) {
    return avatarByPlayerId[participant.playerId];
  }
  return null;
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant} myRow
 * @param {import('./matchHistoryTypes').MatchParticipant[]} allForMatch
 * @param {Record<string, string>} nameByPlayerId
 */
export function formatOpponentsLabel(myRow, allForMatch, nameByPlayerId = {}) {
  const myTeam = myRow?.team;
  const opponents = (allForMatch || []).filter((p) => p.team !== myTeam);
  if (!opponents.length) return "Opponent";
  return opponents.map((p) => participantDisplayName(p, nameByPlayerId)).join(" & ");
}

/**
 * First token of a display name for compact history lines.
 * @param {string} [displayName]
 */
export function firstNameFromDisplayName(displayName) {
  const s = (displayName || "").trim();
  if (!s) return "Player";
  const token = s.split(/\s+/)[0];
  return token || "Player";
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant} myRow
 * @param {import('./matchHistoryTypes').MatchParticipant} other
 */
function isSameParticipantRow(myRow, other) {
  if (!myRow || !other) return false;
  if (myRow.participantId && other.participantId) {
    return myRow.participantId === other.participantId;
  }
  if (myRow.playerId && other.playerId) {
    return myRow.playerId === other.playerId;
  }
  return false;
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant} myRow
 * @param {import('./matchHistoryTypes').MatchParticipant[]} allForMatch
 * @param {Record<string, string>} nameByPlayerId
 */
export function formatTeammateFirstNames(myRow, allForMatch, nameByPlayerId = {}) {
  const myTeam = myRow?.team;
  const onSide = (allForMatch || []).filter((p) => p.team === myTeam);
  if (onSide.length <= 1) return [];
  const partners = onSide.filter((p) => !isSameParticipantRow(myRow, p));
  return partners.map((p) =>
    firstNameFromDisplayName(participantDisplayName(p, nameByPlayerId))
  );
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant} myRow
 * @param {import('./matchHistoryTypes').MatchParticipant[]} allForMatch
 * @param {Record<string, string>} nameByPlayerId
 */
export function formatOpponentsFirstNames(myRow, allForMatch, nameByPlayerId = {}) {
  const myTeam = myRow?.team;
  const opponents = (allForMatch || []).filter((p) => p.team !== myTeam);
  if (!opponents.length) return ["opponents"];
  return opponents.map((p) =>
    firstNameFromDisplayName(participantDisplayName(p, nameByPlayerId))
  );
}

/**
 * e.g. "You won vs Rahul", "You and Alex won vs Sam & Jordan"
 * @param {import('./matchHistoryTypes').MatchParticipant} myRow
 * @param {import('./matchHistoryTypes').MatchParticipant[]} allForMatch
 * @param {Record<string, string>} [nameByPlayerId]
 */
export function formatMatchResultHeadline(myRow, allForMatch, nameByPlayerId = {}) {
  const isWin = myRow?.result === "Win";
  const verb = isWin ? "won" : "lost";
  const oppPart = formatOpponentsFirstNames(myRow, allForMatch, nameByPlayerId).join(" & ");
  const teammates = formatTeammateFirstNames(myRow, allForMatch, nameByPlayerId);
  if (!teammates.length) {
    return `You ${verb} vs ${oppPart}`;
  }
  return `You and ${teammates.join(" & ")} ${verb} vs ${oppPart}`;
}

/**
 * Card team row: "You" or "You & Akshat"
 */
export function formatMyTeamDisplayLine(myRow, allForMatch, nameByPlayerId = {}) {
  const partners = formatTeammateFirstNames(myRow, allForMatch, nameByPlayerId);
  if (!partners.length) return "You";
  return `You & ${partners.join(" & ")}`;
}

/**
 * Card team row: opponent first names joined with &
 */
export function formatOpponentsTeamDisplayLine(myRow, allForMatch, nameByPlayerId = {}) {
  const names = formatOpponentsFirstNames(myRow, allForMatch, nameByPlayerId);
  if (names.length === 1 && names[0] === "opponents") return "Opponents";
  return names.join(" & ");
}

/**
 * Partner label from card line "You & Akshat" → "Akshat"
 * @param {string} [myTeamLine]
 */
export function partnerLabelFromMyTeamLine(myTeamLine) {
  if (!myTeamLine || myTeamLine === "You") return null;
  const m = String(myTeamLine).match(/^You\s*&\s*(.+)$/i);
  return m ? m[1].trim() : null;
}

/**
 * Landing / profile activity row (vs opponents, meta, set pills, duration).
 * @param {object} card from buildMatchCardViewModel
 */
export function buildMatchActivityPreviewFromCard(card) {
  const opponents = card.opponentsTeamLine || card.opponentsLabel || "Opponents";
  const title = `vs ${opponents}`;
  const partner = partnerLabelFromMyTeamLine(card.myTeamLine);
  const typeLabel =
    card.matchTypeLabel || formatMatchTypeLabel(card.matchType) || "Match";
  const time =
    card.completedTimeLabel ||
    (card.completedAt
      ? new Date(card.completedAt).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        })
      : "");
  const metaParts = [];
  if (partner) metaParts.push(`with ${partner}`);
  metaParts.push(typeLabel, time);

  let setPills = (card.setColumns || []).map((col) => ({
    label: `${col.my}-${col.opp}`,
    iWonSet: Boolean(col.iWonSet),
  }));
  if (!setPills.length && card.scoreSummary) {
    setPills = parseSetColumnsFromSummary(
      card.scoreSummary,
      card.myTeam || "A"
    ).map((col) => ({
      label: `${col.my}-${col.opp}`,
      iWonSet: col.iWonSet,
    }));
  }

  return {
    title,
    metaLine: metaParts.filter(Boolean).join(" · "),
    setPills,
    durationLabel: card.durationLabel || null,
    isWin: Boolean(card.isWin),
  };
}

/**
 * @param {string} myTeam A | B
 * @param {Array<{ scoreA?: number; scoreB?: number; winner?: string }>} sets
 */
export function buildSetScoreColumns(myTeam, sets) {
  const side = myTeam === "B" ? "B" : "A";
  return (sets || []).map((set) => {
    const scoreA = Number(set?.scoreA ?? 0);
    const scoreB = Number(set?.scoreB ?? 0);
    const my = side === "B" ? scoreB : scoreA;
    const opp = side === "B" ? scoreA : scoreB;
    const setWinner = set?.winner;
    const iWonSet =
      setWinner === "A" || setWinner === "B"
        ? setWinner === side
        : my > opp;
    return { my, opp, iWonSet };
  });
}

/**
 * @param {string} summary e.g. "21-17, 19-21"
 * @param {string} myTeam
 */
export function parseSetColumnsFromSummary(summary, myTeam) {
  if (!summary || typeof summary !== "string") return [];
  const side = myTeam === "B" ? "B" : "A";
  const parts = summary.split(",").map((p) => p.trim()).filter(Boolean);
  /** @type {Array<{ my: number; opp: number; iWonSet: boolean }>} */
  const cols = [];
  for (const part of parts) {
    const m = part.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (!m) continue;
    const scoreA = parseInt(m[1], 10);
    const scoreB = parseInt(m[2], 10);
    const my = side === "B" ? scoreB : scoreA;
    const opp = side === "B" ? scoreA : scoreB;
    cols.push({ my, opp, iWonSet: my > opp });
  }
  return cols;
}

/**
 * @param {import('./matchHistoryTypes').MatchParticipant[]} participants
 * @param {Record<string, string>} nameByPlayerId
 */
export function groupParticipantsByTeam(participants, nameByPlayerId = {}) {
  const teamA = [];
  const teamB = [];
  for (const p of participants || []) {
    const label = participantDisplayName(p, nameByPlayerId);
    if (p.team === "B") teamB.push({ ...p, displayName: label });
    else teamA.push({ ...p, displayName: label });
  }
  teamA.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  teamB.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return { teamA, teamB };
}

/**
 * @template {{ completedAt: number }} T
 * @param {T[]} items
 * @returns {Array<{ key: string; label: string; items: T[] }>}
 */
export function groupMatchHistoryByDate(items) {
  /** @type {Array<{ key: string; label: string; items: T[] }>} */
  const groups = [];
  let current = null;
  for (const item of items || []) {
    const key = matchHistoryDayKey(item.completedAt);
    if (!current || current.key !== key) {
      current = {
        key,
        label: formatMatchHistoryGroupLabel(item.completedAt),
        items: [],
      };
      groups.push(current);
    }
    current.items.push(item);
  }
  return groups;
}

/**
 * @param {Object} input
 * @param {import('./matchHistoryTypes').MatchParticipant} input.myParticipant
 * @param {import('./matchHistoryTypes').MatchRecord | null} [input.record]
 * @param {import('./matchHistoryTypes').MatchParticipant[]} [input.allParticipants]
 * @param {Record<string, string>} [input.nameByPlayerId]
 */
export function buildMatchCardViewModel(input) {
  const {
    myParticipant,
    record,
    allParticipants = [],
    nameByPlayerId = {},
    sessionRoomId: sessionRoomIdInput,
    sessionRoomLabel: sessionRoomLabelInput,
  } = input;
  const completedAt = myParticipant?.completedAtMs ?? myParticipant?.completedAt ?? 0;
  const completedAtMs =
    typeof completedAt === "number"
      ? completedAt
      : typeof completedAt?.toMillis === "function"
        ? completedAt.toMillis()
        : 0;

  const opponentsLabel = formatOpponentsLabel(myParticipant, allParticipants, nameByPlayerId);
  const resultHeadline = formatMatchResultHeadline(
    myParticipant,
    allParticipants,
    nameByPlayerId
  );
  const isWin = myParticipant?.result === "Win";
  const myTeam = myParticipant?.team === "B" ? "B" : "A";
  const myTeamLine = formatMyTeamDisplayLine(myParticipant, allParticipants, nameByPlayerId);
  const opponentsTeamLine = formatOpponentsTeamDisplayLine(
    myParticipant,
    allParticipants,
    nameByPlayerId
  );
  let setColumns = buildSetScoreColumns(myTeam, record?.sets);
  if (!setColumns.length) {
    setColumns = parseSetColumnsFromSummary(
      myParticipant?.scoreSummary || record?.result || "",
      myTeam
    );
  }

  const sportId = myParticipant?.sportId || record?.sportId || null;
  const cardBase = {
    matchId: myParticipant.matchId,
    sportId,
    matchType: myParticipant.matchType || record?.matchType || "",
    matchTypeLabel: formatMatchTypeLabel(myParticipant.matchType || record?.matchType),
    matchTypeUpper: (myParticipant.matchType || record?.matchType || "match")
      .toString()
      .toUpperCase(),
    participantResult: myParticipant?.result || "",
    isWin,
    myTeam,
    myTeamLine,
    opponentsTeamLine,
    setColumns,
    opponentsLabel,
    resultHeadline,
    scoreSummary: myParticipant?.scoreSummary || record?.result || "",
    finalScore: myParticipant?.scoreSummary || "",
    completedAt: completedAtMs,
    completedAtLabel: formatMatchDate(completedAtMs),
    completedTimeLabel: formatMatchTime(completedAtMs),
    durationSec: record?.durationSec ?? null,
    durationLabel: formatMatchDuration(record?.durationSec),
    competitionId: myParticipant?.competitionId ?? record?.competitionId ?? null,
    clubId: myParticipant?.clubId ?? record?.clubId ?? null,
    sessionRoomId: sessionRoomIdInput ?? record?.sessionRoomId ?? null,
    sessionRoomLabel: sessionRoomLabelInput ?? null,
    arenaLabel:
      sessionRoomLabelInput ||
      record?.arenaName ||
      record?.sessionRoomLabel ||
      sessionRoomIdInput ||
      record?.sessionRoomId ||
      null,
    /** Reserved for statistics / ratings / insights UI slots */
    extensionSlots: {
      competitionBadge: Boolean(myParticipant?.competitionId ?? record?.competitionId),
      clubBadge: Boolean(myParticipant?.clubId ?? record?.clubId),
      tournamentBadge: Boolean(myParticipant?.competitionId ?? record?.competitionId),
      ratingChange: null,
      aiInsights: null,
    },
  };

  cardBase.quickStats = buildCardQuickStats(cardBase);
  return cardBase;
}
